import React, { useState, useCallback } from 'react';

import { TabScrollButton, type TabsProps as MUITabsProps } from '@mui/material';

import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';

import { RebrandTabs, StyledTabs, TabSliderTrack, TabsContainer } from './tabs-rebrand.styles';

type TabsProps = {
  children?: MUITabsProps['children'];
  hideTrack?: boolean;
  legacyTabs?: boolean;
  marginBottom?: '0px' | '32px' | '48px';
  onChange?: MUITabsProps['onChange'];
  orientation?: MUITabsProps['orientation'];
  scrollButtons?: MUITabsProps['scrollButtons'];
  sx?: MUITabsProps['sx'];
  value?: MUITabsProps['value'];
  variant?: MUITabsProps['variant'];
};

export function Tabs(props: TabsProps) {
  const isTabsScrollButtonsFixEnabled = useLDFlag(LD.TABS_SCROLL_BUTTONS_FIX, false);

  const [scrollButtonsVisible, setScrollButtonsVisible] = useState(!isTabsScrollButtonsFixEnabled);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const {
    variant,
    marginBottom,
    orientation,
    value,
    children,
    onChange,
    scrollButtons,
    sx,
    hideTrack,
    legacyTabs = true,
  } = props;

  const mountedRef = useCallback(
    (node) => {
      if (windowWidth && isTabsScrollButtonsFixEnabled) {
        setScrollButtonsVisible(Boolean(node));
      } else if (windowWidth && node !== null) {
        // Original logic: Check for scroll buttons with windowWidth dependency
        setScrollButtonsVisible(Boolean(document.querySelector('.MuiTabs-scrollButtons')));
      }
    },
    [windowWidth, isTabsScrollButtonsFixEnabled]
  );

  function watchWindowSize() {
    setWindowWidth(window.innerWidth);
  }

  window.onresize = watchWindowSize;

  const ScrollButtonComponent = useCallback((props) => <TabScrollButton {...props} ref={mountedRef} />, [mountedRef]);
  return (
    <TabsContainer $marginBottom={marginBottom} $variant={variant}>
      {legacyTabs ? (
        <>
          <RebrandTabs
            $scrollButtonsVisible={!!scrollButtonsVisible}
            $variant={variant}
            children={children}
            orientation={orientation}
            ScrollButtonComponent={ScrollButtonComponent}
            scrollButtons={scrollButtons}
            sx={sx}
            value={value ?? null}
            variant='scrollable'
            onChange={onChange}
          />
          {hideTrack ? null : <TabSliderTrack />}
        </>
      ) : (
        <StyledTabs
          $scrollButtonsVisible={!!scrollButtonsVisible}
          $variant={variant}
          children={children}
          orientation={orientation}
          ScrollButtonComponent={ScrollButtonComponent}
          scrollButtons={scrollButtons}
          sx={sx}
          value={value ?? null}
          variant='scrollable'
          onChange={onChange}
        />
      )}
    </TabsContainer>
  );
}
